import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Edit2, Trash2, Package, Wrench, RefreshCcw, IndianRupee, Users, X, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIES = ["phones", "laptops", "audio", "wearables", "tv", "gaming", "accessories"];
const ORDER_STATUSES = ["placed", "packed", "out_for_delivery", "delivered", "cancelled"];
const BUYBACK_STATUSES = ["pending", "scheduled", "picked_up", "completed", "cancelled"];
const SERVICE_STATUSES = ["requested", "confirmed", "in_progress", "completed", "cancelled"];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20 text-neutral-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="admin-forbidden">
        <h1 className="font-display text-3xl font-bold">Access denied</h1>
        <p className="text-neutral-400 mt-2">You don't have admin privileges.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-page">
      <p className="vm-overline">Admin panel</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 tracking-tighter">Mission control</h1>

      <div className="flex gap-2 mt-8 border-b border-white/10 overflow-x-auto">
        {["overview", "products", "orders", "buybacks", "services"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm font-semibold capitalize border-b-2 transition-colors whitespace-nowrap ${
              tab === t ? "border-volt-500 text-white" : "border-transparent text-neutral-500 hover:text-white"
            }`}
            data-testid={`admin-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && <Overview />}
        {tab === "products" && <Products />}
        {tab === "orders" && <Orders />}
        {tab === "buybacks" && <Buybacks />}
        {tab === "services" && <Services />}
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <div className="text-neutral-400">Loading…</div>;
  const cards = [
    { l: "Revenue", v: inr(stats.revenue), icon: IndianRupee, color: "text-volt-400" },
    { l: "Orders", v: stats.orders, icon: Package, color: "text-volt-400" },
    { l: "Pending orders", v: stats.pending_orders, icon: Package, color: "text-blaze-400" },
    { l: "Products", v: stats.products, icon: Package, color: "text-volt-400" },
    { l: "Buyback pending", v: stats.buyback_pending, icon: RefreshCcw, color: "text-blaze-400" },
    { l: "Service pending", v: stats.service_pending, icon: Wrench, color: "text-blaze-400" },
    { l: "Users", v: stats.users, icon: Users, color: "text-neutral-300" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.l} className="vm-card p-5" data-testid={`admin-stat-${c.l.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-400 uppercase tracking-wider font-semibold">{c.l}</p>
            <c.icon className={`h-4 w-4 ${c.color}`} />
          </div>
          <p className="font-display text-3xl font-bold mt-3">{c.v}</p>
        </div>
      ))}
    </div>
  );
}

function Products() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} for new | product
  const [showForm, setShowForm] = useState(false);

  const refresh = () => api.get("/products").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-neutral-400">{items.length} products</p>
        <button
          className="vm-btn-primary inline-flex items-center gap-2 text-sm"
          onClick={() => { setEditing({}); setShowForm(true); }}
          data-testid="admin-add-product"
        >
          <Plus className="h-4 w-4" /> Add product
        </button>
      </div>

      <div className="vm-card overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead className="bg-ink-800 text-xs uppercase tracking-wider text-neutral-400">
            <tr>
              <th className="text-left p-3">Product</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Price</th>
              <th className="text-right p-3">Stock</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.product_id} className="border-t border-white/5 hover:bg-white/5">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                    <div>
                      <p className="font-medium text-white">{p.name}</p>
                      <p className="text-xs text-neutral-500">{p.brand}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3 capitalize text-neutral-300">{p.category}</td>
                <td className="p-3 text-right font-mono">{inr(p.price)}</td>
                <td className="p-3 text-right font-mono">{p.stock}</td>
                <td className="p-3 text-right">
                  <button className="text-neutral-400 hover:text-white mr-2" onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`admin-edit-${p.product_id}`}>
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button className="text-neutral-400 hover:text-blaze-400" onClick={() => onDelete(p.product_id)} data-testid={`admin-delete-${p.product_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ProductForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.product_id);
  const [form, setForm] = useState({
    name: initial?.name || "",
    brand: initial?.brand || "",
    category: initial?.category || "phones",
    price: initial?.price || 0,
    mrp: initial?.mrp || 0,
    stock: initial?.stock || 0,
    image: initial?.image || "",
    description: initial?.description || "",
    is_featured: initial?.is_featured || false,
    specs: initial?.specs ? JSON.stringify(initial.specs) : "{}",
  });
  const setF = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    let parsedSpecs = {};
    try { parsedSpecs = JSON.parse(form.specs || "{}"); } catch { toast.error("Specs must be valid JSON"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        mrp: Number(form.mrp),
        stock: Number(form.stock),
        specs: parsedSpecs,
      };
      if (isEdit) await api.put(`/admin/products/${initial.product_id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="admin-product-form">
      <div className="bg-ink-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
        <div className="p-6 flex items-center justify-between border-b border-white/10 sticky top-0 bg-ink-900 z-10">
          <h2 className="font-display text-xl font-bold">{isEdit ? "Edit product" : "Add product"}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-white/5 flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="text-xs text-neutral-400">Name *</label><input className="vm-input mt-1" value={form.name} onChange={setF("name")} required data-testid="pf-name" /></div>
          <div><label className="text-xs text-neutral-400">Brand *</label><input className="vm-input mt-1" value={form.brand} onChange={setF("brand")} required data-testid="pf-brand" /></div>
          <div>
            <label className="text-xs text-neutral-400">Category</label>
            <select className="vm-input mt-1" value={form.category} onChange={setF("category")} data-testid="pf-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-neutral-400">Price *</label><input type="number" className="vm-input mt-1" value={form.price} onChange={setF("price")} required data-testid="pf-price" /></div>
          <div><label className="text-xs text-neutral-400">MRP *</label><input type="number" className="vm-input mt-1" value={form.mrp} onChange={setF("mrp")} required data-testid="pf-mrp" /></div>
          <div><label className="text-xs text-neutral-400">Stock</label><input type="number" className="vm-input mt-1" value={form.stock} onChange={setF("stock")} data-testid="pf-stock" /></div>
          <label className="flex items-center gap-2 text-sm mt-7">
            <input type="checkbox" checked={form.is_featured} onChange={setF("is_featured")} data-testid="pf-featured" /> Featured
          </label>
          <div className="sm:col-span-2"><label className="text-xs text-neutral-400">Image URL *</label><input className="vm-input mt-1" value={form.image} onChange={setF("image")} required data-testid="pf-image" /></div>
          <div className="sm:col-span-2"><label className="text-xs text-neutral-400">Description</label><textarea className="vm-input mt-1 min-h-[80px]" value={form.description} onChange={setF("description")} data-testid="pf-description" /></div>
          <div className="sm:col-span-2"><label className="text-xs text-neutral-400">Specs (JSON)</label><textarea className="vm-input mt-1 min-h-[80px] font-mono text-xs" value={form.specs} onChange={setF("specs")} data-testid="pf-specs" /></div>
          <div className="sm:col-span-2 flex gap-3 mt-2">
            <button type="button" className="vm-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={submitting} className="vm-btn-primary flex-1" data-testid="pf-submit">
              {submitting ? "Saving..." : (isEdit ? "Save changes" : "Create product")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const refresh = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => { refresh(); }, []);
  const updateStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Updated");
    refresh();
  };
  return (
    <div className="space-y-3" data-testid="admin-orders-list">
      {orders.length === 0 ? <p className="text-neutral-500 text-sm">No orders yet.</p> :
        orders.map((o) => (
          <div key={o.order_id} className="vm-card p-5 flex flex-wrap items-center gap-4">
            <div className="flex -space-x-3">
              {o.items.slice(0, 3).map((it, i) => <img key={i} src={it.image} alt="" className="h-10 w-10 rounded-lg border-2 border-ink-900 object-cover" />)}
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-xs font-mono text-neutral-500">{o.order_id}</p>
              <p className="text-sm font-semibold">{o.items.length} items • {inr(o.total)}</p>
              <p className="text-xs text-neutral-400 mt-0.5">{o.address.full_name} • {o.address.city}</p>
            </div>
            <select
              className="vm-input w-auto text-sm"
              value={o.status}
              onChange={(e) => updateStatus(o.order_id, e.target.value)}
              data-testid={`admin-order-status-${o.order_id}`}
            >
              {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        ))
      }
    </div>
  );
}

function Buybacks() {
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { urls: string[], index: number, request_id: string }
  const refresh = () => api.get("/admin/buyback/requests").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);
  const updateStatus = async (id, status) => {
    await api.put(`/admin/buyback/requests/${id}/status`, { status });
    toast.success("Updated");
    refresh();
  };
  return (
    <div className="space-y-3" data-testid="admin-buybacks-list">
      {items.length === 0 ? <p className="text-neutral-500 text-sm">No buyback requests yet.</p> :
        items.map((b) => {
          const photos = Array.isArray(b.photos) ? b.photos.filter((p) => p && typeof p === "object" && p.storage_path) : [];
          return (
            <div key={b.request_id} className="vm-card p-5" data-testid={`admin-buyback-${b.request_id}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-blaze-500/15 flex items-center justify-center shrink-0"><RefreshCcw className="h-5 w-5 text-blaze-400" /></div>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-semibold">{b.brand} {b.model} <span className="text-neutral-500 font-normal">• {b.condition?.replace("_", " ")}</span></p>
                  <p className="text-xs text-neutral-400 mt-0.5">Quote: <span className="font-display font-bold text-blaze-400">{inr(b.quoted_price)}</span> • Pickup: {new Date(b.pickup_slot).toLocaleString()}</p>
                  <p className="text-[11px] font-mono text-neutral-500 mt-0.5">{b.request_id}</p>
                </div>
                <select className="vm-input w-auto text-sm" value={b.status} onChange={(e) => updateStatus(b.request_id, e.target.value)} data-testid={`admin-buyback-status-${b.request_id}`}>
                  {BUYBACK_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              {photos.length > 0 ? (
                <div className="mt-4 flex gap-2 flex-wrap" data-testid={`admin-buyback-photos-${b.request_id}`}>
                  {photos.map((_, idx) => (
                    <BuybackThumb
                      key={idx}
                      requestId={b.request_id}
                      index={idx}
                      onClick={async () => {
                        const urls = await Promise.all(photos.map((_, i) => fetchPhotoUrl(b.request_id, i)));
                        setLightbox({ urls, index: idx, request_id: b.request_id });
                      }}
                    />
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-xs text-neutral-500 flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> No photos uploaded</p>
              )}
            </div>
          );
        })
      }
      {lightbox && <Lightbox {...lightbox} onClose={() => setLightbox(null)} onNav={(i) => setLightbox((l) => ({ ...l, index: i }))} />}
    </div>
  );
}

async function fetchPhotoUrl(requestId, index) {
  const res = await api.get(`/admin/buyback/photo`, { params: { request_id: requestId, index }, responseType: "blob" });
  return URL.createObjectURL(res.data);
}

function BuybackThumb({ requestId, index, onClick }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revoked = false;
    let objUrl = null;
    fetchPhotoUrl(requestId, index)
      .then((u) => { if (!revoked) { setUrl(u); objUrl = u; } else URL.revokeObjectURL(u); })
      .catch(() => setErr(true));
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [requestId, index]);
  if (err) return <div className="h-20 w-20 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[10px] text-neutral-500">N/A</div>;
  if (!url) return <div className="h-20 w-20 rounded-xl bg-white/5 border border-white/10 animate-pulse" />;
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-20 w-20 rounded-xl overflow-hidden border border-white/10 hover:border-volt-500 transition-colors"
      data-testid={`admin-buyback-thumb-${requestId}-${index}`}
    >
      <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
    </button>
  );
}

function Lightbox({ urls, index, onClose, onNav }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNav((index - 1 + urls.length) % urls.length);
      if (e.key === "ArrowRight") onNav((index + 1) % urls.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, urls.length, onClose, onNav]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-buyback-lightbox"
    >
      <button onClick={onClose} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="lightbox-close"><X className="h-5 w-5" /></button>
      <button
        onClick={(e) => { e.stopPropagation(); onNav((index - 1 + urls.length) % urls.length); }}
        className="absolute left-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <img
        src={urls[index]}
        alt={`Buyback photo ${index + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={(e) => { e.stopPropagation(); onNav((index + 1) % urls.length); }}
        className="absolute right-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
        data-testid="lightbox-next"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
        {index + 1} / {urls.length}
      </div>
    </div>
  );
}

function Services() {
  const [items, setItems] = useState([]);
  const refresh = () => api.get("/admin/services/bookings").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);
  const updateStatus = async (id, status) => {
    await api.put(`/admin/services/bookings/${id}/status`, { status });
    toast.success("Updated");
    refresh();
  };
  return (
    <div className="space-y-3" data-testid="admin-services-list">
      {items.length === 0 ? <p className="text-neutral-500 text-sm">No service bookings yet.</p> :
        items.map((s) => (
          <div key={s.booking_id} className="vm-card p-5 flex flex-wrap items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-volt-500/15 flex items-center justify-center"><Wrench className="h-5 w-5 text-volt-400" /></div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-semibold capitalize">{s.service_type} • {s.brand} {s.model}</p>
              <p className="text-xs text-neutral-400 mt-0.5">{s.issue?.slice(0, 80)} • Slot: {new Date(s.slot).toLocaleString()}</p>
            </div>
            <select className="vm-input w-auto text-sm" value={s.status} onChange={(e) => updateStatus(s.booking_id, e.target.value)} data-testid={`admin-svc-status-${s.booking_id}`}>
              {SERVICE_STATUSES.map((st) => <option key={st} value={st}>{st.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        ))
      }
    </div>
  );
}
